import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import Paste from "@mui/icons-material/ContentPasteGo";
import { ReactComponent as Copy } from "../../../assets/icons/copy.svg";
import { Pagination, Navigation } from "swiper";
import { fullSizeUnit, fullUnit, units } from "../../../helpers/helpers";
import AntSwitch from "./AntSwitch";
import { useDispatch } from "react-redux";
import { showSnackBar } from "../../../store/common/snackBarSlice";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import TimePickerModal from "./TimePickerModal";
import { activeQuestions } from "../../../constants/zones";
import { useTranslation } from "react-i18next";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const FieldLoading = () => {
  return (
    <div className="w-[10px] h-[10px] border-2 border-ui-text-muted border-opacity-70 border-t-transparent border-l-transparent rounded-full animate-spin [animation-duration:0.8s]"></div>
  );
};
const Zones = ({
  data,
  rangeData,
  dimensions,
  cod,
  protection,
  setData,
  setRangeData,
  setDimensions,
  setCod,
  setProtection,
  loadingField,
  setLoadingField,
  activeQuestion,
  hoverQuestion,
  focused,
  setFocused,
  zones,
  questions = [],
  zonePerPage,
  handleTempSave,
  onDataChange,
}) => {
  const { t } = useTranslation("carrier");
  const dispatch = useDispatch();
  const [timepickerModal, setTimepickerModal] = useState();

  const InputValidator = (value) => {
    if (value !== "" && value !== undefined && value !== null) {
      // Allow numbers with decimal points, including single zero
      const match = value.match(/^(\d+\.?\d*|\.\d+|0)$/);
      return match ? match[0] : "";
    } else {
      return "";
    }
  };

  const replicateZone = (idx) => {
    let temp = {};
    setData((data) => {
      return data.map((dataItem) => {
        // Replicate main zoneData
        const newZoneData = dataItem.zoneData.map((zItem, index) => {
          if (idx === index) {
            temp = zItem;
            return zItem;
          }
          if (idx + 1 === index) {
            return {
              ...temp,
              is_active: temp.enabled ? 1 : 0,
              zone_id: zItem.zone_id,
            };
          } else {
            return zItem;
          }
        });

        // Replicate rangeData (weight)
        let newRangeData = dataItem.rangeData;
        if (Array.isArray(dataItem.rangeData)) {
          newRangeData = dataItem.rangeData.map((rItem) => {
            return {
              ...rItem,
              zoneData: rItem.zoneData.map((zItem, index) => {
                if (idx + 1 === index) {
                  return {
                    ...zItem,
                    price: rItem.zoneData[idx].price,
                    enabled: rItem.zoneData[idx].enabled,
                    is_active: rItem.zoneData[idx].enabled ? 1 : 0,
                  };
                } else {
                  return zItem;
                }
              }),
            };
          });
        }

        // Replicate dimensions (size)
        let newDimensions = dataItem.dimensions;
        if (Array.isArray(dataItem.dimensions)) {
          newDimensions = dataItem.dimensions.map((dimItem) => {
            return {
              ...dimItem,
              zoneData: dimItem.zoneData.map((zItem, index) => {
                if (idx + 1 === index) {
                  return {
                    ...zItem,
                    price: dimItem.zoneData[idx].price,
                    enabled: dimItem.zoneData[idx].enabled,
                    is_active: dimItem.zoneData[idx].enabled ? 1 : 0,
                  };
                } else {
                  return zItem;
                }
              }),
            };
          });
        }

        return {
          ...dataItem,
          zoneData: newZoneData,
          rangeData: newRangeData,
          dimensions: newDimensions,
        };
      });
    });
  };

  const replicateZoneField = (idx, qIndex) => {
    let temp = {};
    setData((data) => {
      return data.map((dataItem) => {
        if (String(dataItem.question_id) === String(qIndex)) {
          // Replicate main zoneData
          const newZoneData = dataItem.zoneData.map((zItem, index) => {
            if (idx === index) {
              temp = zItem;
              return zItem;
            }
            if (idx + 1 === index) {
              return {
                ...temp,
                is_active: temp.enabled ? 1 : 0,
                zone_id: zItem.zone_id,
              };
            } else {
              return zItem;
            }
          });

          // Replicate rangeData (weight)
          let newRangeData = dataItem.rangeData;
          if (Array.isArray(dataItem.rangeData)) {
            newRangeData = dataItem.rangeData.map((rItem) => ({
              ...rItem,
              zoneData: rItem.zoneData.map((zItem, index) => {
                if (idx + 1 === index) {
                  return {
                    ...zItem,
                    price: rItem.zoneData[idx].price,
                    enabled: rItem.zoneData[idx].enabled,
                    is_active: rItem.zoneData[idx].enabled ? 1 : 0,
                  };
                } else {
                  return zItem;
                }
              }),
            }));
          }

          // Replicate dimensions (size)
          let newDimensions = dataItem.dimensions;
          if (Array.isArray(dataItem.dimensions)) {
            newDimensions = dataItem.dimensions.map((dimItem) => ({
              ...dimItem,
              zoneData: dimItem.zoneData.map((zItem, index) => {
                if (idx + 1 === index) {
                  return {
                    ...zItem,
                    price: dimItem.zoneData[idx].price,
                    enabled: dimItem.zoneData[idx].enabled,
                    is_active: dimItem.zoneData[idx].enabled ? 1 : 0,
                  };
                } else {
                  return zItem;
                }
              }),
            }));
          }

          // Replicate cod
          let newCod = dataItem.cod;
          if (Array.isArray(dataItem.cod)) {
            newCod = dataItem.cod.map((codItem) => ({
              ...codItem,
              zoneData: codItem.zoneData.map((zItem, index) => {
                if (idx + 1 === index) {
                  return {
                    ...zItem,
                    price: codItem.zoneData[idx].price,
                    enabled: codItem.zoneData[idx].enabled,
                    is_active: codItem.zoneData[idx].enabled ? 1 : 0,
                  };
                } else {
                  return zItem;
                }
              }),
            }));
          }

          // Replicate shipmentProtectionRange
          let newProtection = dataItem.shipmentProtectionRange;
          if (Array.isArray(dataItem.shipmentProtectionRange)) {
            newProtection = dataItem.shipmentProtectionRange.map(
              (protItem) => ({
                ...protItem,
                zoneData: protItem.zoneData.map((zItem, index) => {
                  if (idx + 1 === index) {
                    return {
                      ...zItem,
                      price: protItem.zoneData[idx].price,
                      enabled: protItem.zoneData[idx].enabled,
                      is_active: protItem.zoneData[idx].enabled ? 1 : 0,
                    };
                  } else {
                    return zItem;
                  }
                }),
              })
            );
          }

          return {
            ...dataItem,
            zoneData: newZoneData,
            rangeData: newRangeData,
            dimensions: newDimensions,
            cod: newCod,
            shipmentProtectionRange: newProtection,
          };
        } else {
          return dataItem;
        }
      });
    });
  };

  const replicateRangeDataField = (idx, questId, rdIndex) => {
    setData((data) => {
      return data.map((dataItem) => {
        if (String(dataItem.question_id) === String(questId)) {
          return {
            ...dataItem,
            rangeData: dataItem.rangeData.map((rItem, rIdx) => {
              if (rIdx === rdIndex) {
                return {
                  ...rItem,
                  zoneData: rItem.zoneData.map((zItem, zIdx) =>
                    zIdx === idx + 1
                      ? {
                          ...zItem,
                          price: rItem.zoneData[idx].price,
                          enabled: rItem.zoneData[idx].enabled,
                          is_active: rItem.zoneData[idx].enabled ? 1 : 0,
                        }
                      : zItem
                  ),
                };
              }
              return rItem;
            }),
          };
        }
        return dataItem;
      });
    });
  };
  const replicateDimensionsField = (idx, qIndex, dmIndex) => {
    setData((data) => {
      return data.map((dataItem) => {
        if (String(dataItem.question_id) === String(qIndex)) {
          return {
            ...dataItem,
            dimensions: dataItem.dimensions.map((dimItem, dIdx) => {
              if (dIdx === dmIndex) {
                return {
                  ...dimItem,
                  zoneData: dimItem.zoneData.map((zItem, zIdx) =>
                    zIdx === idx + 1
                      ? {
                          ...zItem,
                          price: dimItem.zoneData[idx].price,
                          enabled: dimItem.zoneData[idx].enabled,
                          is_active: dimItem.zoneData[idx].enabled ? 1 : 0,
                        }
                      : zItem
                  ),
                };
              }
              return dimItem;
            }),
          };
        }
        return dataItem;
      });
    });
  };
  const replicateCodField = (idx, qIndex, rIndex) => {
    setCod((pData) => {
      return pData.map((pItem, pIndex) => {
        if (rIndex === pIndex) {
          return {
            ...pItem,
            zoneData: pItem.zoneData.map((zItem, zIdx) => {
              if (idx + 1 === zIdx) {
                return {
                  ...zItem,
                  price: pItem.zoneData[idx].price,
                  enabled: pItem.zoneData[idx].enabled,
                  is_active: pItem.zoneData[idx].enabled ? 1 : 0,
                };
              } else {
                return zItem;
              }
            }),
          };
        } else {
          return pItem;
        }
      });
    });
  };
  const replicateProtectionField = (idx, qIndex, rIndex) => {
    setProtection((pData) => {
      return pData.map((pItem, pIndex) => {
        if (rIndex === pIndex) {
          return {
            ...pItem,
            zoneData: pItem.zoneData.map((zItem, zIdx) => {
              if (idx + 1 === zIdx) {
                return {
                  ...zItem,
                  price: pItem.zoneData[idx].price,
                  enabled: pItem.zoneData[idx].enabled,
                  is_active: pItem.zoneData[idx].enabled ? 1 : 0,
                };
              } else {
                return zItem;
              }
            }),
          };
        } else {
          return pItem;
        }
      });
    });
  };
  const handleZoneProtectionFieldChecked = (
    e,
    idx,
    dmIndex,
    item,
    quest,
    unit
  ) => {
    setProtection((pData) => {
      return pData.map((pItem, pIndex) => {
        return dmIndex === pIndex
          ? {
              ...pItem,
              zoneData: zones.map((zone, zIdx) => {
                const zItem = pItem.zoneData[zIdx];
                return zIdx === idx
                  ? {
                      ...zItem,
                      zone_id: item.id,
                      enabled: e.target.checked,
                      is_active: e.target.checked ? 1 : 0,
                    }
                  : zItem;
              }),
            }
          : pItem;
      });
    });

    // Get the current data to access the correct protection data
    const currentData = data.find(
      (dItem) => String(dItem.question_id) === String(quest.id)
    );
    const currentProtection = currentData?.shipmentProtectionRange?.[dmIndex];

    dispatch(
      showSnackBar({
        text: t(
          e.target.checked
            ? "zoneSettings.enabledInZoneForPrice"
            : "zoneSettings.disabledInZoneForPrice",
          {
            name: quest.name,
            zone: item.name,
            min: `${currentProtection?.min || 0}${unit}`,
            max: `${currentProtection?.max || 0}${unit}`,
          },
        ),
        severity: "info",
      })
    );
  };
  // Handler for toggling simple/number/time_range fields
  const handleZoneFieldChecked = (e, idx, index, item, quest) => {
    setData((d) => {
      return d.map((dItem) => {
        return String(dItem.question_id) === String(quest.id)
          ? {
              ...dItem,
              zoneData: dItem.zoneData.map((zdata, zIndex) =>
                zIndex === idx
                  ? {
                      ...zdata,
                      zone_id: item.id,
                      enabled: e.target.checked,
                      is_active: e.target.checked ? 1 : 0,
                    }
                  : zdata
              ),
            }
          : dItem;
      });
    });
    dispatch(
      showSnackBar({
        text: t(
          e.target.checked
            ? "zoneSettings.enabledForZone"
            : "zoneSettings.disabledForZone",
          { name: quest.name, zone: item.name },
        ),
        severity: "info",
      })
    );
  };

  // Handler for toggling weight fields
  const handleZoneWeightFieldChecked = (e, idx, rdIndex, item, quest, unit) => {
    setData((d) => {
      return d.map((dItem) => {
        if (String(dItem.question_id) === String(quest.id)) {
          return {
            ...dItem,
            rangeData: dItem.rangeData.map((rItem, rIndex) =>
              rIndex === rdIndex
                ? {
                    ...rItem,
                    zoneData: rItem.zoneData.map((zItem, zIdx) =>
                      zIdx === idx
                        ? {
                            ...zItem,
                            zone_id: item.id,
                            enabled: e.target.checked,
                            is_active: e.target.checked ? 1 : 0,
                          }
                        : zItem
                    ),
                  }
                : rItem
            ),
          };
        } else {
          return dItem;
        }
      });
    });
    setTimeout(() => {
      // Get the current data to access the correct range data
      const currentData = data.find(
        (dItem) => String(dItem.question_id) === String(quest.id)
      );
      const currentRange = currentData?.rangeData?.[rdIndex];

      dispatch(
        showSnackBar({
          text: t(
            e.target.checked
              ? "zoneSettings.enabledForWeight"
              : "zoneSettings.disabledForWeight",
            {
              name: item.name,
              min: `${currentRange?.min || 0}${unit}`,
              max: `${currentRange?.max || 0}${unit}`,
            },
          ),
          severity: "info",
        })
      );
    }, 500);
  };

  // Handler for toggling size fields
  const handleZoneSizeFieldChecked = (e, idx, dmIndex, item, quest, unit) => {
    setData((d) => {
      return d.map((dItem) => {
        if (String(dItem.question_id) === String(quest.id)) {
          return {
            ...dItem,
            dimensions: dItem.dimensions.map((dimItem, dIndex) =>
              dIndex === dmIndex
                ? {
                    ...dimItem,
                    zoneData: dimItem.zoneData.map((zItem, zIdx) =>
                      zIdx === idx
                        ? {
                            ...zItem,
                            zone_id: item.id,
                            enabled: e.target.checked,
                            is_active: e.target.checked ? 1 : 0,
                          }
                        : zItem
                    ),
                  }
                : dimItem
            ),
          };
        } else {
          return dItem;
        }
      });
    });
    setTimeout(() => {
      // Get the current data to access the correct dimensions
      const currentData = data.find(
        (dItem) => String(dItem.question_id) === String(quest.id)
      );
      const currentDimension = currentData?.dimensions?.[dmIndex];

      dispatch(
        showSnackBar({
          text: t(
            e.target.checked
              ? "zoneSettings.enabledForSize"
              : "zoneSettings.disabledForSize",
            {
              name: item.name,
              from: `${currentDimension?.min_length || 0}${unit}-${
                currentDimension?.min_width || 0
              }${unit}-${currentDimension?.min_height || 0}${unit}`,
              to: `${currentDimension?.max_length || 0}${unit}-${
                currentDimension?.max_width || 0
              }${unit}-${currentDimension?.max_height || 0}${unit}`,
            },
          ),
          severity: "info",
        })
      );
    }, 500);
  };

  // Handler for toggling COD fields
  const handleZoneCodFieldChecked = (e, idx, dmIndex, item, quest, unit) => {
    setData((d) => {
      return d.map((dItem) => {
        if (String(dItem.question_id) === String(quest.id)) {
          return {
            ...dItem,
            cod: dItem.cod.map((codItem, cIdx) =>
              cIdx === dmIndex
                ? {
                    ...codItem,
                    zoneData: codItem.zoneData.map((zItem, zIdx) =>
                      zIdx === idx
                        ? {
                            ...zItem,
                            zone_id: item.id,
                            enabled: e.target.checked,
                            is_active: e.target.checked ? 1 : 0,
                          }
                        : zItem
                    ),
                  }
                : codItem
            ),
          };
        } else {
          return dItem;
        }
      });
    });
    // Get the current data to access the correct cod data
    const currentData = data.find(
      (dItem) => String(dItem.question_id) === String(quest.id)
    );
    const currentCod = currentData?.cod?.[dmIndex];

    dispatch(
      showSnackBar({
        text: t(
          e.target.checked
            ? "zoneSettings.enabledInZoneForPrice"
            : "zoneSettings.disabledInZoneForPrice",
          {
            name: quest.name,
            zone: item.name,
            min: `${currentCod?.min || 0}${unit}`,
            max: `${currentCod?.max || 0}${unit}`,
          },
        ),
        severity: "info",
      })
    );
  };
  // Example: Initialize zoneData if empty
  useEffect(() => {
    if (
      data.length > 0 &&
      data.some((item) => !item.zoneData || item.zoneData.length === 0)
    ) {
      const initializedData = data.map((item) => ({
        ...item,
        zoneData: zones.map((zone, index) => ({
          zone_id: zone.id,
          enabled: false,
          is_active: 0,
          price: "",
          ...(typeof item?.zoneData?.[index] === "object"
            ? item?.zoneData?.[index]
            : {}),
        })),
      }));
      setData(initializedData);
    }
  }, [zones]);

  return (
    <div className="zstable_content_right">
      <TimePickerModal
        isOpen={timepickerModal}
        onClose={() => {
          setTimepickerModal(false);
          handleTempSave();
        }}
        data={data}
        setData={setData}
      />
      <Swiper
        spaceBetween={16}
        slidesPerView="auto"
        pagination={{
          type: "progressbar",
        }}
        freeMode={true}
        watchOverflow={true}
        loop={false}
        // navigation={true}
        modules={[Pagination, Navigation]}
        className="mySwiper mx-2 px-2"
      >
        {zones.map((item, idx) => (
          <SwiperSlide key={`zone-${idx}`} className="!w-fit">
            <div className="zstcr_zone_col pb-12">
              <div className="zstc_col_heading mb-3">
                {item.name.length < 8 && item.name}
                {item.name.length > 7 && (
                  <LightTooltip placement="top" arrow title={item.name}>
                    <span className="zstc_col_headingwrap">
                      {item.name.substr(0, 7)}
                    </span>
                  </LightTooltip>
                )}
                {item.name.length > 7 && "..."}

                {idx + 1 < zones.length && (
                  <LightTooltip
                    placement="top"
                    arrow
                    title={t("zoneSettings.replicateDataToNextZone")}
                  >
                    <Paste
                      className="zstc_col_headingpasteicon"
                      onClick={() => replicateZone(idx)}
                    />
                  </LightTooltip>
                )}
              </div>
              {questions.map((quest, index) => {
                const currentData = data.find(
                  (item) => String(item.question_id) === String(quest.id)
                );
                return (
                  <div
                    className={`${
                      activeQuestions.includes(quest.id)
                        ? ""
                        : "pointer-events-none opacity-30 select-none cursor-not-allowed"
                    }`}
                  >
                    {quest.type === "simple" && (
                      <div className="zstc_zcol_data">
                        <LightTooltip
                          placement="top"
                          arrow
                          title={t(
                            currentData?.zoneData[idx]?.enabled
                              ? "zoneSettings.disableName"
                              : "zoneSettings.enableName",
                            { name: quest.name },
                          )}
                        >
                          <AntSwitch
                            checked={currentData?.zoneData[idx]?.enabled}
                            onChange={(e) =>
                              handleZoneFieldChecked(e, idx, index, item, quest)
                            }
                          />
                        </LightTooltip>
                        <LightTooltip
                          placement="top"
                          arrow
                          title={t("zoneSettings.priceForName", { name: quest.name })}
                        >
                          <input
                            type="text"
                            className={`zstc_col_datafield ${
                              !currentData?.zoneData[idx]?.enabled
                                ? "zstcc_disabled_datafield"
                                : ""
                            } ${
                              (quest.id === activeQuestion ||
                                quest.id === hoverQuestion) &&
                              "active__question_field"
                            }`}
                            onFocus={() =>
                              setFocused(
                                currentData?.zoneData[idx]?.zone_id +
                                  `${quest.id}` +
                                  `${idx}`
                              )
                            }
                            onBlur={() => {
                              setLoadingField(focused);
                              setFocused("");
                              handleTempSave();
                            }}
                            value={`${
                              ![undefined, null, ""].includes(
                                currentData?.zoneData[idx]?.price
                              )
                                ? currentData?.zoneData[idx]?.price
                                : ""
                            }${
                              focused !==
                                currentData?.zoneData[idx]?.zone_id +
                                  `${quest.id}` +
                                  `${idx}` &&
                              currentData?.zoneData[idx]?.price &&
                              currentData?.zoneData[idx]?.price !== ""
                                ? `${
                                    !["$", "%"].includes(currentData?.unit)
                                      ? `/${currentData?.unit}`
                                      : currentData?.unit
                                  }`
                                : ""
                            }`}
                            placeholder="0$"
                            onChange={(e) => {
                              const val = InputValidator(e.target.value);
                              setData((d) => {
                                return d.map((dItem) => {
                                  return String(dItem.question_id) ===
                                    String(quest.id)
                                    ? {
                                        ...dItem,
                                        zoneData: currentData?.zoneData?.map(
                                          (zdata, zIndex) => {
                                            return zIndex === idx
                                              ? {
                                                  ...zdata,
                                                  zone_id: item.id,
                                                  price: val ?? "",
                                                }
                                              : zdata;
                                          }
                                        ),
                                      }
                                    : dItem;
                                });
                              });
                            }}
                          />
                        </LightTooltip>
                        {currentData?.zoneData[idx]?.zone_id +
                          `${quest.id}` +
                          `${idx}` ==
                          loadingField && (
                          <span className="zstc_col_datafieldloading">
                            <FieldLoading />
                          </span>
                        )}
                        <div className="zstc_col_databottomtext">
                          {t("zoneSettings.per")}
                          {Object.keys(units).includes(
                            currentData?.unit?.toLowerCase()
                          )
                            ? fullUnit(currentData?.unit)
                            : currentData?.type}
                        </div>
                        <LightTooltip
                          placement="top"
                          arrow
                          title={t("zoneSettings.replicateDataToNextZone")}
                        >
                          <Copy
                            className={`zstc_col_datacopyicon ${
                              idx + 1 === zones.length ? "invisible" : ""
                            }`}
                            onClick={() => replicateZoneField(idx, quest.id)}
                          />
                        </LightTooltip>
                      </div>
                    )}
                    {quest.type === "number" && (
                      <div className="zstc_zcol_data">
                        <LightTooltip
                          placement="top"
                          arrow
                          title={t(
                            currentData?.zoneData[idx]?.enabled
                              ? "zoneSettings.disableName"
                              : "zoneSettings.enableName",
                            { name: quest.name },
                          )}
                        >
                          <AntSwitch
                            checked={currentData?.zoneData[idx]?.enabled}
                            onChange={(e) =>
                              handleZoneFieldChecked(e, idx, index, item, quest)
                            }
                          />
                        </LightTooltip>
                        <LightTooltip
                          placement="top"
                          arrow
                          title={`${quest.name}`}
                        >
                          <input
                            type="number"
                            className={`zstc_col_datafield ${
                              !currentData?.zoneData[idx]?.enabled
                                ? "zstcc_disabled_datafield"
                                : ""
                            } ${
                              (quest.id === activeQuestion ||
                                quest.id === hoverQuestion) &&
                              "active__question_field"
                            }`}
                            onFocus={() =>
                              setFocused(
                                currentData?.zoneData[idx]?.zone_id +
                                  `${quest.id}` +
                                  `${idx}`
                              )
                            }
                            onBlur={() => {
                              setLoadingField(focused);
                              setFocused("");
                              handleTempSave();
                            }}
                            value={
                              ![undefined, null, ""].includes(
                                currentData?.zoneData[idx]?.transit_days
                              )
                                ? currentData?.zoneData[idx]?.transit_days
                                : ""
                            }
                            placeholder="0"
                            onChange={(e) => {
                              setData((d) => {
                                return d.map((dItem) => {
                                  return String(dItem.question_id) ===
                                    String(quest.id)
                                    ? {
                                        ...dItem,
                                        zoneData: currentData?.zoneData?.map(
                                          (zdata, zIndex) => {
                                            return zIndex === idx
                                              ? {
                                                  ...zdata,
                                                  zone_id: item.id,
                                                  transit_days:
                                                    e.target.value ?? "",
                                                }
                                              : zdata;
                                          }
                                        ),
                                      }
                                    : dItem;
                                });
                              });
                            }}
                          />
                        </LightTooltip>
                        {currentData?.zoneData[idx]?.zone_id +
                          `${quest.id}` +
                          `${idx}` ==
                          loadingField && (
                          <span className="zstc_col_datafieldloading">
                            <FieldLoading />
                          </span>
                        )}
                        <LightTooltip
                          placement="top"
                          arrow
                          title={t("zoneSettings.replicateDataToNextZone")}
                        >
                          <Copy
                            className={`zstc_col_datacopyicon ${
                              idx + 1 === zones.length ? "invisible" : ""
                            }`}
                            onClick={() => replicateZoneField(idx, quest.id)}
                          />
                        </LightTooltip>
                      </div>
                    )}
                    {quest.type === "time_range" && (
                      <div className="zstc_zcol_data">
                        <LightTooltip
                          placement="top"
                          arrow
                          title={t(
                            currentData?.zoneData[idx]?.enabled
                              ? "zoneSettings.disableName"
                              : "zoneSettings.enableName",
                            { name: quest.name },
                          )}
                        >
                          <AntSwitch
                            checked={currentData?.zoneData[idx]?.enabled}
                            onChange={(e) =>
                              handleZoneFieldChecked(e, idx, index, item, quest)
                            }
                          />
                        </LightTooltip>
                        <LightTooltip
                          placement="top"
                          arrow
                          title={`${quest.name}`}
                        >
                          <button
                            onClick={() =>
                              setTimepickerModal({
                                question: quest,
                                questId: quest.id,
                                zoneIndex: idx,
                                zone: item,
                              })
                            }
                            className={`w-[90px] h-[20px] flex items-center justify-center rounded-sm bg-state-warning-solid text-white text-xs ${
                              !currentData?.zoneData[idx]?.enabled
                                ? "pointer-events-none opacity-60"
                                : ""
                            }`}
                          >
                            {!["", null, undefined].includes(
                              currentData?.zoneData[idx]?.availablity_time_from
                            ) &&
                            !["", null, undefined].includes(
                              currentData?.zoneData[idx]?.availablity_time_to
                            )
                              ? `${currentData?.zoneData[idx]?.availablity_time_from} - ${currentData?.zoneData[idx]?.availablity_time_to}`
                              : t("zoneSettings.selectTime")}
                          </button>
                        </LightTooltip>
                        {currentData?.zoneData[idx]?.zone_id +
                          `${quest.id}` +
                          `${idx}` ==
                          loadingField && (
                          <span className="zstc_col_datafieldloading">
                            <FieldLoading />
                          </span>
                        )}
                        <LightTooltip
                          placement="top"
                          arrow
                          title={t("zoneSettings.replicateDataToNextZone")}
                        >
                          <Copy
                            className={`zstc_col_datacopyicon ${
                              idx + 1 === zones.length ? "invisible" : ""
                            }`}
                            onClick={() => replicateZoneField(idx, quest.id)}
                          />
                        </LightTooltip>
                      </div>
                    )}
                    {quest.type === "weight" &&
                      currentData?.rangeData?.map((rd, rdIndex) => (
                        <div className="zstc_zcol_data mt-[8px]">
                          <LightTooltip
                            placement="top"
                            arrow
                            title={t(
                              rd.zoneData[idx]?.enabled
                                ? "zoneSettings.disableWeight"
                                : "zoneSettings.enableWeight",
                              {
                                min: `${rd.min}${currentData?.unit}`,
                                max: `${rd.max}${currentData?.unit}`,
                              },
                            )}
                          >
                            <AntSwitch
                              checked={rd.zoneData[idx]?.enabled}
                              onChange={(e) =>
                                handleZoneWeightFieldChecked(
                                  e,
                                  idx,
                                  rdIndex,
                                  item,
                                  quest,
                                  currentData?.unit
                                )
                              }
                            />
                          </LightTooltip>
                          <LightTooltip
                            placement="top"
                            arrow
                            title={t("zoneSettings.priceForRange", { min: rd.min, minUnit: currentData?.unit, max: rd.max, maxUnit: currentData?.unit })}
                          >
                            <input
                              type="text"
                              className={`zstc_col_datafield ${
                                !rd.zoneData[idx]?.enabled
                                  ? "zstcc_disabled_datafield"
                                  : ""
                              } ${
                                (quest.id === activeQuestion ||
                                  quest.id === hoverQuestion) &&
                                "active__question_field"
                              }`}
                              onFocus={() =>
                                setFocused(
                                  `${currentData?.rangeData?.[rdIndex]?.zoneData[idx]?.zone_id}` +
                                    `${quest.id}` +
                                    `${rdIndex}`
                                )
                              }
                              onBlur={() => {
                                setLoadingField(focused);
                                setFocused("");
                                handleTempSave();
                              }}
                              value={
                                focused ===
                                `${currentData?.rangeData?.[rdIndex]?.zoneData[idx]?.zone_id}${quest.id}${rdIndex}`
                                  ? rd.zoneData[idx]?.price ?? ""
                                  : ![undefined, null, ""].includes(
                                      rd.zoneData[idx]?.price
                                    )
                                  ? `$${rd.zoneData[idx]?.price}`
                                  : ""
                              }
                              placeholder="0$"
                              onChange={(e) => {
                                const val = InputValidator(e.target.value);
                                setData((d) => {
                                  return d.map((dItem) => {
                                    return String(dItem.question_id) ===
                                      String(quest.id)
                                      ? {
                                          ...dItem,
                                          rangeData: dItem.rangeData.map(
                                            (rItem, rIdx) =>
                                              rIdx === rdIndex
                                                ? {
                                                    ...rItem,
                                                    zoneData:
                                                      rItem.zoneData.map(
                                                        (zItem, zIdx) =>
                                                          zIdx === idx
                                                            ? {
                                                                ...zItem,
                                                                zone_id:
                                                                  item.id,
                                                                price:
                                                                  val ?? "",
                                                              }
                                                            : zItem
                                                      ),
                                                  }
                                                : rItem
                                          ),
                                        }
                                      : dItem;
                                  });
                                });
                              }}
                            />
                          </LightTooltip>
                          {`${currentData?.rangeData?.[rdIndex]?.zoneData[idx]?.zone_id}${quest.id}${rdIndex}` ===
                            loadingField && (
                            <span className="zstc_col_datafieldloading">
                              <FieldLoading />
                            </span>
                          )}
                          <div className="zstc_col_databottomtext">
                            {t("zoneSettings.perSpaced")}
                            {Object.keys(units).includes(
                              currentData?.unit?.toLowerCase()
                            )
                              ? fullUnit(currentData?.unit)
                              : currentData?.type}
                          </div>
                          <LightTooltip
                            placement="top"
                            arrow
                            title={t("zoneSettings.replicateDataToNextZone")}
                          >
                            <Copy
                              className={`zstc_col_datacopyicon ${
                                idx + 1 === zones.length ? "invisible" : ""
                              }`}
                              onClick={() =>
                                replicateRangeDataField(idx, quest.id, rdIndex)
                              }
                            />
                          </LightTooltip>
                        </div>
                      ))}

                    {quest.type === "size" && (
                      <>
                        <div className="mt-[50px]"></div>
                        {currentData?.dimensions?.map((dm, dmIndex) => (
                          <div className="zstc_zcol_data2">
                            {/* <input
                              type="checkbox"
                              className="zstc_col_datacheckbox"
                            /> */}
                            <LightTooltip
                              placement="top"
                              arrow
                              title={t(
                                dm.zoneData[idx]?.enabled
                                  ? "zoneSettings.disableSize"
                                  : "zoneSettings.enableSize",
                                {
                                  from: `${dm.min_length}-${dm.min_width}-${dm.min_height}`,
                                  to: `${dm.max_length}-${dm.max_width}-${dm.max_height}`,
                                  unit: fullSizeUnit(currentData?.unit),
                                },
                              )}
                            >
                              <AntSwitch
                                checked={dm.zoneData[idx]?.enabled}
                                onChange={(e) =>
                                  handleZoneSizeFieldChecked(
                                    e,
                                    idx,
                                    dmIndex,
                                    item,
                                    quest,
                                    currentData?.unit
                                  )
                                }
                              />
                            </LightTooltip>
                            <LightTooltip
                              placement="top"
                              arrow
                              title={t("zoneSettings.priceForSize", {
                                from: `${dm.min_length}-${dm.min_width}-${dm.min_height}`,
                                to: `${dm.max_length}-${dm.max_width}-${dm.max_height}`,
                                unit: fullSizeUnit(currentData?.unit),
                              })}
                            >
                              <input
                                type="text"
                                className={`zstc_col_datafield ${
                                  !dm.zoneData[idx]?.enabled
                                    ? "zstcc_disabled_datafield"
                                    : ""
                                } ${
                                  (quest.id === activeQuestion ||
                                    quest.id === hoverQuestion) &&
                                  "active__question_field"
                                }`}
                                onFocus={() =>
                                  setFocused(
                                    currentData?.dimensions?.[dmIndex]
                                      ?.zoneData[idx]?.zone_id +
                                      `${quest.id}` +
                                      `${dmIndex}` +
                                      `${idx}`
                                  )
                                }
                                onBlur={() => {
                                  setLoadingField(focused);
                                  setFocused("");
                                  handleTempSave();
                                }}
                                value={
                                  focused ===
                                  currentData?.dimensions?.[dmIndex]?.zoneData[
                                    idx
                                  ]?.zone_id +
                                    `${quest.id}` +
                                    `${dmIndex}` +
                                    `${idx}`
                                    ? dm.zoneData[idx]?.price ?? ""
                                    : ![undefined, null, ""].includes(
                                        dm.zoneData[idx]?.price
                                      )
                                    ? `$${dm.zoneData[idx]?.price}`
                                    : ""
                                }
                                placeholder="0$"
                                onChange={(e) => {
                                  const val = InputValidator(e.target.value);
                                  setData((d) => {
                                    const newData = d.map((dItem) => {
                                      return String(dItem.question_id) ===
                                        String(quest.id)
                                        ? {
                                            ...dItem,
                                            dimensions: dItem.dimensions.map(
                                              (dimItem, dIdx) =>
                                                dIdx === dmIndex
                                                  ? {
                                                      ...dimItem,
                                                      zoneData:
                                                        dimItem.zoneData.map(
                                                          (zItem, zIdx) =>
                                                            zIdx === idx
                                                              ? {
                                                                  ...zItem,
                                                                  zone_id:
                                                                    item.id,
                                                                  price:
                                                                    val ?? "",
                                                                }
                                                              : zItem
                                                        ),
                                                    }
                                                  : dimItem
                                            ),
                                          }
                                        : dItem;
                                    });
                                    return newData;
                                  });
                                  // Trigger sync state change
                                  if (onDataChange) {
                                    onDataChange();
                                  }
                                }}
                              />
                            </LightTooltip>
                            {currentData?.dimensions?.[dmIndex]?.zoneData[idx]
                              ?.zone_id +
                              `${quest.id}` +
                              `${dmIndex}` +
                              `${idx}` ===
                              loadingField && (
                              <span className="zstc_col_datafieldloading pb-1.5">
                                <FieldLoading />
                              </span>
                            )}
                            <div className="zstc_col_databottomtext mt-1">
                              {t("zoneSettings.perSpaced")}
                              {Object.keys(units).includes(
                                currentData?.unit?.toLowerCase()
                              )
                                ? fullUnit(currentData?.unit)
                                : currentData?.type}
                            </div>
                            <LightTooltip
                              placement="top"
                              arrow
                              title={t("zoneSettings.replicateDataToNextZone")}
                            >
                              <Copy
                                className={`zstc_col_datacopyicon ${
                                  idx + 1 === zones.length ? "invisible" : ""
                                }`}
                                onClick={() =>
                                  replicateDimensionsField(
                                    idx,
                                    quest.id,
                                    dmIndex
                                  )
                                }
                              />
                            </LightTooltip>
                          </div>
                        ))}
                      </>
                    )}
                    {quest.type === "cod" &&
                      currentData?.cod?.map((dm, dmIndex) => (
                        <div className="zstc_zcol_data3">
                          {/* <input
                                type="checkbox"
                                className="zstc_col_datacheckbox"
                              /> */}
                          <LightTooltip
                            placement="top"
                            arrow
                            title={t(
                              dm.zoneData[idx]?.enabled
                                ? "zoneSettings.disableCod"
                                : "zoneSettings.enableCod",
                              {
                                min: `${dm.min}${currentData?.unit}`,
                                max: `${dm.max}${currentData?.unit}`,
                              },
                            )}
                          >
                            <AntSwitch
                              checked={dm.zoneData[idx]?.enabled}
                              onChange={(e) =>
                                handleZoneCodFieldChecked(
                                  e,
                                  idx,
                                  dmIndex,
                                  item,
                                  quest,
                                  currentData?.unit
                                )
                              }
                            />
                          </LightTooltip>
                          <LightTooltip
                            placement="top"
                            arrow
                            title={t("zoneSettings.extraCodFor", { min: dm.min, minUnit: currentData?.unit, max: dm.max, maxUnit: currentData?.unit })}
                          >
                            <input
                              type="text"
                              className={`zstc_col_datafield ${
                                !dm.zoneData[idx]?.enabled
                                  ? "zstcc_disabled_datafield"
                                  : ""
                              } ${
                                (quest.id === activeQuestion ||
                                  quest.id === hoverQuestion) &&
                                "active__question_field"
                              }`}
                              onFocus={() =>
                                setFocused(
                                  currentData?.cod?.[dmIndex]?.zoneData[idx]
                                    ?.zone_id +
                                    `${quest.id}` +
                                    `${dmIndex}` +
                                    `${idx}`
                                )
                              }
                              onBlur={() => {
                                setLoadingField(focused);
                                setFocused("");
                                handleTempSave();
                              }}
                              value={`$${
                                ![undefined, null, ""].includes(
                                  dm.zoneData[idx]?.price
                                )
                                  ? dm.zoneData[idx]?.price
                                  : ""
                              }`}
                              placeholder="0$"
                              onChange={(e) => {
                                const val = InputValidator(e.target.value);
                                setData((d) => {
                                  const newData = d.map((dItem) => {
                                    return String(dItem.question_id) ===
                                      String(quest.id)
                                      ? {
                                          ...dItem,
                                          cod: dItem.cod.map((codItem, cIdx) =>
                                            cIdx === dmIndex
                                              ? {
                                                  ...codItem,
                                                  zoneData:
                                                    codItem.zoneData.map(
                                                      (zItem, zIdx) =>
                                                        zIdx === idx
                                                          ? {
                                                              ...zItem,
                                                              zone_id: item.id,
                                                              price: val ?? "",
                                                            }
                                                          : zItem
                                                    ),
                                                }
                                              : codItem
                                          ),
                                        }
                                      : dItem;
                                  });
                                  return newData;
                                });
                                // Trigger sync state change
                                if (onDataChange) {
                                  onDataChange();
                                }
                              }}
                            />
                          </LightTooltip>
                          {currentData?.cod?.[dmIndex]?.zoneData[idx]?.zone_id +
                            `${quest.id}` +
                            `${dmIndex}` +
                            `${idx}` ===
                            loadingField && (
                            <span className="zstc_col_datafieldloading">
                              <FieldLoading />
                            </span>
                          )}
                          <div className="zstc_col_databottomtext">
                            {t("zoneSettings.perSpaced")}
                            {Object.keys(units).includes(
                              currentData?.unit?.toLowerCase()
                            )
                              ? fullUnit(currentData?.unit)
                              : currentData?.type}
                          </div>
                          <LightTooltip
                            placement="top"
                            arrow
                            title={t("zoneSettings.replicateDataToNextZone")}
                          >
                            <Copy
                              className={`zstc_col_datacopyicon ${
                                idx + 1 === zones.length ? "invisible" : ""
                              }`}
                              onClick={() =>
                                replicateCodField(idx, index, dmIndex)
                              }
                            />
                          </LightTooltip>
                        </div>
                      ))}
                    {quest.type === "protection" &&
                      currentData?.shipmentProtectionRange?.map(
                        (dm, dmIndex) => (
                          <div className="zstc_zcol_data3">
                            {/* <input
                                type="checkbox"
                                className="zstc_col_datacheckbox"
                              /> */}
                            <LightTooltip
                              placement="top"
                              arrow
                              title={t(
                                dm.zoneData[idx]?.enabled
                                  ? "zoneSettings.disableProtection"
                                  : "zoneSettings.enableProtection",
                                {
                                  min: `${dm.min}${currentData?.unit}`,
                                  max: `${dm.max}${currentData?.unit}`,
                                },
                              )}
                            >
                              <AntSwitch
                                checked={dm.zoneData[idx]?.enabled}
                                onChange={(e) =>
                                  handleZoneProtectionFieldChecked(
                                    e,
                                    idx,
                                    dmIndex,
                                    item,
                                    quest,
                                    currentData?.unit
                                  )
                                }
                              />
                            </LightTooltip>
                            <LightTooltip
                              placement="top"
                              arrow
                              title={t("zoneSettings.extraProtectionFor", { min: dm.min, minUnit: currentData?.unit, max: dm.max, maxUnit: currentData?.unit })}
                            >
                              <input
                                type="text"
                                className={`zstc_col_datafield ${
                                  !dm.zoneData[idx]?.enabled
                                    ? "zstcc_disabled_datafield"
                                    : ""
                                } ${
                                  (quest.id === activeQuestion ||
                                    quest.id === hoverQuestion) &&
                                  "active__question_field"
                                }`}
                                onFocus={() =>
                                  setFocused(
                                    currentData?.shipmentProtectionRange?.[
                                      dmIndex
                                    ]?.zoneData[idx]?.zone_id +
                                      `${quest.id}` +
                                      `${dmIndex}` +
                                      `${idx}`
                                  )
                                }
                                onBlur={() => {
                                  setLoadingField(focused);
                                  setFocused("");
                                  handleTempSave();
                                }}
                                value={`$${
                                  ![undefined, null, ""].includes(
                                    dm.zoneData[idx]?.price
                                  )
                                    ? dm.zoneData[idx]?.price
                                    : ""
                                }`}
                                placeholder="0$"
                                onChange={(e) => {
                                  const val = InputValidator(e.target.value);
                                  setData((d) => {
                                    const newData = d.map((dItem) => {
                                      return String(dItem.question_id) ===
                                        String(quest.id)
                                        ? {
                                            ...dItem,
                                            shipmentProtectionRange:
                                              dItem.shipmentProtectionRange.map(
                                                (pItem, pIdx) =>
                                                  pIdx === dmIndex
                                                    ? {
                                                        ...pItem,
                                                        zoneData:
                                                          pItem.zoneData.map(
                                                            (zItem, zIdx) =>
                                                              zIdx === idx
                                                                ? {
                                                                    ...zItem,
                                                                    zone_id:
                                                                      item.id,
                                                                    price:
                                                                      val ?? "",
                                                                  }
                                                                : zItem
                                                          ),
                                                      }
                                                    : pItem
                                              ),
                                          }
                                        : dItem;
                                    });
                                    return newData;
                                  });
                                  // Trigger sync state change
                                  if (onDataChange) {
                                    onDataChange();
                                  }
                                }}
                              />
                            </LightTooltip>
                            {currentData?.shipmentProtectionRange?.[dmIndex]
                              ?.zoneData[idx]?.zone_id +
                              `${quest.id}` +
                              `${dmIndex}` +
                              `${idx}` ===
                              loadingField && (
                              <span className="zstc_col_datafieldloading">
                                <FieldLoading />
                              </span>
                            )}
                            <div className="zstc_col_databottomtext">
                              {t("zoneSettings.perSpaced")}
                              {Object.keys(units).includes(
                                currentData?.unit?.toLowerCase()
                              )
                                ? fullUnit(currentData?.unit)
                                : currentData?.type}
                            </div>
                            <LightTooltip
                              placement="top"
                              arrow
                              title={t("zoneSettings.replicateDataToNextZone")}
                            >
                              <Copy
                                className={`zstc_col_datacopyicon ${
                                  idx + 1 === zones.length ? "invisible" : ""
                                }`}
                                onClick={() =>
                                  replicateProtectionField(idx, index, dmIndex)
                                }
                              />
                            </LightTooltip>
                          </div>
                        )
                      )}
                  </div>
                );
              })}
            </div>
          </SwiperSlide>
        ))}
        <SwiperSlide>
          <div className="zstcontent_zone_placeholder"></div>
        </SwiperSlide>
      </Swiper>
    </div>
  );
};

export default Zones;
